import { isCategoricalAxis } from '../../../types/guards.js';
import {
  type XYChartAxisDirection,
  XYChartAxisInternal,
  type XYChartOrientation,
  type XYChartStackingValueBoundaries,
} from '../../../types/xy-chart-internals.js';
import {
  BAR_VARIANT_STACK,
  type BarDatapoint,
  type BarSeriesVariant,
} from '../../../types/xy-chart.js';

const orientationToBaselineAxis: Record<'vertical' | 'horizontal', 'x' | 'y'> =
  {
    vertical: 'x',
    horizontal: 'y',
  };

export const getCombinedAxesOrientationKey = (
  xAxisId: string,
  yAxisId: string,
  chartOrientation: 'vertical' | 'horizontal',
) => `${xAxisId}-${yAxisId}-${chartOrientation}`;

export const getStackingData = (
  chartOrientation: 'vertical' | 'horizontal',
  datapoint: BarDatapoint,
  xAxis: XYChartAxisInternal,
  yAxis: XYChartAxisInternal,
  /** Precomputed `xAxisId-yAxisId-orientation` key. Pass it when the same triple is resolved many times to skip the per-call template literal. */
  precomputedAxisOrientationKey?: string,
) => {
  const [start, end] =
    chartOrientation === 'vertical'
      ? [datapoint.x0, datapoint.x1]
      : [datapoint.y0, datapoint.y1];

  const axisOrientationKey =
    precomputedAxisOrientationKey ??
    getCombinedAxesOrientationKey(xAxis.id, yAxis.id, chartOrientation);

  const stackKey =
    (end ? `${start}-${end}` : `${start}`) + `-${axisOrientationKey}`;

  const value = chartOrientation === 'vertical' ? datapoint.y1 : datapoint.x1;

  return { stackKey, value };
};

export const getBaselineCoordinate = (
  orientation?: XYChartOrientation,
): XYChartAxisDirection => {
  if (!orientation || orientation === 'both') {
    return;
  }

  return orientationToBaselineAxis[orientation];
};

export const isStackable = (
  variant: BarSeriesVariant,
  chartOrientation: 'vertical' | 'horizontal' | 'both' | undefined,
  xAxis: XYChartAxisInternal,
  yAxis: XYChartAxisInternal,
) => {
  const baselineCoordinate = getBaselineCoordinate(chartOrientation);

  // We check for the opposite axis to the baselineCoordinate because the stacking axis is opposite to the primary axis (baseline)
  const isStackingAxisCategorical =
    baselineCoordinate && baselineCoordinate === 'x'
      ? isCategoricalAxis(yAxis)
      : isCategoricalAxis(xAxis);

  return (
    chartOrientation !== 'both' &&
    variant === BAR_VARIANT_STACK &&
    !isStackingAxisCategorical
  );
};

export const getStackedValue = (
  value: number,
  prevValue: XYChartStackingValueBoundaries,
) => {
  return {
    minBoundary:
      value < 0 ? prevValue.minBoundary + value : prevValue.minBoundary,
    maxBoundary:
      value > 0 ? prevValue.maxBoundary + value : prevValue.maxBoundary,
  };
};
